import { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './CameraOval.module.css';

interface Props {
  onCapture: (blob: Blob) => void;
}

type State = 'init' | 'live' | 'denied' | 'unsupported';

export function CameraOval({ onCapture }: Props): JSX.Element {
  const { t } = useTranslation('flows');
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [state, setState] = useState<State>('init');
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('user');

  const startCamera = useCallback(async (facing: 'user' | 'environment') => {
    if (!navigator.mediaDevices?.getUserMedia) { setState('unsupported'); return; }
    streamRef.current?.getTracks().forEach(tr => tr.stop());
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: facing } });
      streamRef.current = stream;
      if (videoRef.current) { videoRef.current.srcObject = stream; void videoRef.current.play(); }
      setState('live');
    } catch {
      setState('denied');
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (!navigator.mediaDevices?.getUserMedia) { setState('unsupported'); return; }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: facingMode } });
        if (cancelled) { stream.getTracks().forEach(tr => tr.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) { videoRef.current.srcObject = stream; void videoRef.current.play(); }
        setState('live');
      } catch {
        setState('denied');
      }
    })();
    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach(tr => tr.stop());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const takePhoto = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    const size = 1024;
    const canvas = document.createElement('canvas');
    canvas.width = size; canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    // Center-square crop from the source video.
    const sw = v.videoWidth, sh = v.videoHeight;
    const side = Math.min(sw, sh);
    const sx = (sw - side) / 2, sy = (sh - side) / 2;
    ctx.drawImage(v, sx, sy, side, side, 0, 0, size, size);
    canvas.toBlob((blob) => { if (blob) onCapture(blob); }, 'image/jpeg', 0.9);
  }, [onCapture]);

  const flipCamera = useCallback(() => {
    const nextFacing = facingMode === 'user' ? 'environment' : 'user';
    setFacingMode(nextFacing);
    void startCamera(nextFacing);
  }, [facingMode, startCamera]);

  const openGallery = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (ev) => {
      const f = (ev.target as HTMLInputElement).files?.[0];
      if (f) onCapture(f);
    };
    input.click();
  }, [onCapture]);

  const isDenied = state === 'denied' || state === 'unsupported';
  const isLive = state === 'live';

  return (
    <div className={styles.viewfinder}>
      {/* Video positioned inside a sized container */}
      {isDenied ? (
        <div className={styles.deniedViewfinder}>
          <p className={styles.deniedText}>{t('onboarding.capture.permissionDenied')}</p>
        </div>
      ) : (
        <div className={styles.ovalContainer}>
          <video
            ref={videoRef}
            className={styles.video}
            playsInline
            muted
            style={{ transform: facingMode === 'user' ? 'scaleX(-1)' : 'none' }}
          />
      </div>
      )}

      {/* Bottom action row: Flip · Take photo · Upload from gallery */}
      <div className={styles.actions}>
        <button
          type="button"
          className={styles.linkBtn}
          onClick={flipCamera}
          disabled={!isLive}
          aria-label={t('onboarding.capture.flip')}
        >
          {t('onboarding.capture.flip')}
        </button>

        <button
          type="button"
          className={styles.captureBtn}
          onClick={takePhoto}
          disabled={!isLive}
          aria-label={t('onboarding.capture.title')}
        >
          <span className={styles.captureRing} aria-hidden="true" />
          <span className={styles.captureDot} aria-hidden="true" />
        </button>

        <button
          type="button"
          className={styles.linkBtn}
          onClick={openGallery}
          aria-label={t('onboarding.capture.fallback')}
        >
          {t('onboarding.capture.fallback')}
        </button>
      </div>
    </div>
  );
}
